import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../Auth';
import UserAvatar from '../users/UserAvatar';
import LogoutButton from './LogoutButton';

const Header = ({ onVisibilityChange }) => {
  const { user, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [isVisible, setIsVisible] = useState(true);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Check if we're on an event detail page
  const isEventDetailPage = location.pathname.startsWith('/event/') && 
                            !location.pathname.endsWith('/edit') && 
                            !location.pathname.endsWith('/ics');

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle header visibility on event detail pages
  useEffect(() => {
    if (isEventDetailPage) {
      // Start hidden on event pages
      setIsVisible(false);
      if (onVisibilityChange) onVisibilityChange(false);

      let lastScrollY = window.scrollY;
      let ticking = false;
      const scrollThreshold = 5; // Minimum pixels to scroll before toggling

      const handleScroll = () => {
        if (!ticking) {
          window.requestAnimationFrame(() => {
            const currentScrollY = window.scrollY;
            const scrollDiff = Math.abs(currentScrollY - lastScrollY);
            
            // Only process if scrolled enough to avoid jitter
            if (scrollDiff < scrollThreshold) {
              ticking = false;
              return;
            }
            
            // Always hide if at the very top
            if (currentScrollY < 10) {
              setIsVisible(false);
              if (onVisibilityChange) onVisibilityChange(false);
            }
            // Show when scrolling up (and past threshold)
            else if (currentScrollY < lastScrollY && currentScrollY > 100) {
              setIsVisible(true);
              if (onVisibilityChange) onVisibilityChange(true);
            }
            // Hide when scrolling down
            else if (currentScrollY > lastScrollY && currentScrollY > 100) {
              setIsVisible(false);
              if (onVisibilityChange) onVisibilityChange(false);
            }
            
            lastScrollY = currentScrollY;
            ticking = false;
          });
          ticking = true;
        }
      };

      window.addEventListener('scroll', handleScroll, { passive: true });
      return () => window.removeEventListener('scroll', handleScroll);
    } else {
      // Always visible on other pages
      setIsVisible(true);
      if (onVisibilityChange) onVisibilityChange(true);
    }
  }, [location.pathname, isEventDetailPage, onVisibilityChange]);

  const isEventListPage = location.pathname === '/' || location.pathname === '/events';

  const renderUserMenu = () => (
    <div className="relative" ref={menuRef}>
      <button onClick={toggleMenu} className="flex items-center focus:outline-none text-gray-700 hover:text-gray-900 transition-colors">
        <UserAvatar user={user} size="sm" className="cursor-pointer" />
        <span className="ml-2 hidden md:inline font-medium">{user.name || user.email}</span>
      </button>
      {isMenuOpen && (
        <div className="absolute right-0 mt-2 w-48 bg-white/95 backdrop-blur-md rounded-xl shadow-soft-lg border border-gray-200/50 py-1 z-10">
          <Link to="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors" onClick={() => setIsMenuOpen(false)}>My Profile</Link>
          <Link to="/my-events" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors" onClick={() => setIsMenuOpen(false)}>My Events</Link>
          <LogoutButton className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors" />
        </div>
      )}
    </div>
  );

  return (
    <header 
      className={`bg-white fixed top-0 left-0 right-0 shadow-soft border-b border-gray-200 transition-transform duration-300 ${
        isVisible ? 'translate-y-0' : '-translate-y-full'
      }`}
      style={{ zIndex: 'var(--z-header)' }}
    >
      <div className="container mx-auto px-4">
        <div className="flex justify-between items-center py-4">
          <Link to={isAuthenticated ? "/my-events" : "/"} className="flex items-center gap-2 sm:gap-3 hover:opacity-80 transition-opacity">
            <img 
              src="/gatherly_512.png" 
              alt="Gatherly" 
              className="h-8 w-8 rounded-lg"
            />
            <span className="text-xl sm:text-2xl font-bold tracking-tight text-gray-900">Gatherly</span>
          </Link>
          {process.env.NODE_ENV === 'development' && (
            <div className="text-sm bg-amber-400 text-gray-900 px-2 py-1 rounded-md font-medium">
              dev mode
            </div>
          )}
          <nav className="flex items-center gap-2 sm:gap-4">
            {isAuthenticated && (
              <Link
                to="/create-event"
                className="bg-blue-600 text-white py-2 px-3 sm:px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 font-medium shadow-sm hover:shadow text-sm sm:text-base"
              >
                Add Event
              </Link>
            )}
            {isAuthenticated ? renderUserMenu() : (
              <div className="flex items-center gap-2 sm:gap-3">
                <Link 
                  to="/login?mode=signin" 
                  className="text-gray-700 hover:text-blue-600 font-medium transition-colors duration-200 text-sm sm:text-base"
                >
                  Sign In
                </Link>
                <Link 
                  to="/login?mode=signup" 
                  className="bg-blue-600 text-white py-2 px-3 sm:px-4 rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-all duration-200 font-medium shadow-sm hover:shadow text-sm sm:text-base whitespace-nowrap"
                >
                  Sign Up
                </Link>
              </div>
            )}
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Header;
